import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStickyNote, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NOTE_COLOR_STYLES, useSymbolNotes } from '../contexts/SymbolNotesContext';

const COLORS = ['amber', 'mint', 'rose', 'slate'];

/**
 * Inline sticky note editor for a single symbol (used in quote popup).
 */
const SymbolStickyNote = ({ symbol, compact = false }) => {
  const { getNote, setNote, removeNote } = useSymbolNotes();
  const existing = getNote(symbol);
  const [text, setText] = useState(existing?.text || '');
  const [color, setColor] = useState(existing?.color || 'amber');
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    const note = getNote(symbol);
    setText(note?.text || '');
    setColor(note?.color || 'amber');
    setSavedFlash(false);
    // Only rehydrate when the symbol changes; avoid clobbering in-progress typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  useEffect(() => {
    if (!symbol) return undefined;
    const timer = setTimeout(() => {
      const current = getNote(symbol);
      const trimmed = text.trim();
      const sameText = (current?.text || '') === trimmed;
      const sameColor = (current?.color || 'amber') === color;
      if (sameText && (sameColor || !trimmed)) return;
      setNote(symbol, trimmed, color);
      if (trimmed) {
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 1200);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [text, color, symbol, getNote, setNote]);

  const styles = NOTE_COLOR_STYLES[color] || NOTE_COLOR_STYLES.amber;

  return (
    <div className={`rounded-md border ${styles.panel} overflow-hidden`}>
      <div className="flex items-center justify-between gap-2 px-3 pt-2.5 pb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <FontAwesomeIcon icon={faStickyNote} className="text-[var(--lt-warning)] text-xs" />
          <span className="lt-label">{compact ? 'Note' : 'Session note'}</span>
          {savedFlash && <span className="text-[10px] text-bull">Saved</span>}
        </div>
        <div className="flex items-center gap-1">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              title={c}
              onClick={() => setColor(c)}
              className={`h-4 w-4 rounded-full border ${
                color === c ? 'border-ink scale-110' : 'border-hairline opacity-70 hover:opacity-100'
              } ${NOTE_COLOR_STYLES[c].chip}`}
              aria-label={`Note color ${c}`}
            />
          ))}
          {existing?.text && (
            <button
              type="button"
              onClick={() => {
                setText('');
                removeNote(symbol);
              }}
              className="ml-1 h-7 w-7 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:text-bear hover:bg-elevated"
              title="Delete note"
            >
              <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
            </button>
          )}
        </div>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 2000))}
        rows={compact ? 3 : 4}
        placeholder={`Sticky note for ${symbol}…`}
        className="w-full resize-y min-h-[72px] max-h-[180px] bg-transparent px-3 pb-2.5 pt-1 text-xs text-ink placeholder:text-muted outline-none border-0"
      />
      <div className="px-3 pb-2 flex items-center justify-between text-[10px] text-muted">
        <span>{text.length}/2000</span>
        {existing?.updatedAt && (
          <span>
            Updated{' '}
            {(() => {
              try {
                return new Date(existing.updatedAt).toLocaleString();
              } catch {
                return '';
              }
            })()}
          </span>
        )}
      </div>
    </div>
  );
};

export default SymbolStickyNote;
